import { X } from "lucide-react";
import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { api } from '../api/client';
import { ALL_MCP_SERVERS } from '../api/mcp';
import { useAuth, ADMIN_EMAILS } from '../hooks/useAuth';
import { logToApi, type LogType } from '../lib/logApi';
import { pushToast, dismissToastByKey } from '../lib/toastStore';
import type { RbacConfig, SessionInfo } from '../api/types';
import './NewSessionDialog.css';
import { useInstanceMode } from '../hooks/useInstanceMode';
import { serviceOrigin } from '../lib/services';

// Model change with restart + verification (Cloud backend 5d976b7): the PATCH now
// auto-restarts a running agent (--continue, history kept) and probes the CLI's
// /model picker for the ACTIVE model. Runs async after the dialog closes — progress
// + verdict live in the global toast stack. Verification contract:
//   verified:true            → active_model confirmed (green)
//   status applies_on_next_start → agent suspended/dead, config saved (info)
//   status agent_not_ready   → poll GET /model-verify (same shape)
//   verified:false           → CLI kept a DIFFERENT model, e.g. CLI too old (warn/red)
interface ModelVerification {
  verified?: boolean;
  active_model?: string;
  requested?: string;
  status?: string;
  detail?: string;
  agent_state?: string;
}
async function runModelChange(base: string, sessionName: string, cfgPatch: Record<string, unknown>) {
  const key = `model-change:${sessionName}`;
  const requested = String(cfgPatch.model || '(Standard)');
  // Every verdict is MANUAL-dismiss: a model change is rare + important, and an
  // auto-hiding result vanished before Alex could read it (2026-07-03 "Meldung
  // kam, ist weg wieder"). Click ✕ = confirmed-read.
  const finish = (variant: 'success' | 'error' | 'info', content: string) => {
    dismissToastByKey(key);
    pushToast({ dedupKey: key, variant, dismiss: 'manual', content });
  };
  dismissToastByKey(key);
  // manual (closable), NOT persist: a slow verify chain (remote probes measured ~30s
  // each) can keep this up for minutes — Alex must be able to dismiss it (2026-07-03).
  pushToast({
    dedupKey: key, variant: 'pending', dismiss: 'manual',
    content: `${sessionName}: Modell wird auf ${prettyModel(String(cfgPatch.model || '') || 'Standard')} umgestellt — Restart + Verifikation… (bis ~60 s)`,
  });
  try {
    // Client timeout ≥60s: normal ~8-15s, heavy agent up to ~45s (Cloud contract).
    const ctrl = new AbortController();
    const t = setTimeout(() => ctrl.abort(), 90_000);
    const res = await fetch(`${base}/api/sessions/${sessionName}/config`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(cfgPatch),
      signal: ctrl.signal,
    });
    clearTimeout(t);
    const d: { model_verification?: ModelVerification } = await res.json().catch(() => ({}));
    if (!res.ok) { finish('error', `${sessionName}: Modellwechsel fehlgeschlagen (HTTP ${res.status}).`); return; }
    const v = d.model_verification;
    if (v?.verified) { finish('success', `✓ ${sessionName}: aktiv ${v.active_model || requested}`); return; }
    if (v?.status === 'applies_on_next_start') {
      finish('info', `${sessionName}: Modell gespeichert — greift beim nächsten Start.`);
      return;
    }
    // Restart landed the agent on the login gate (Anthropic reauth breaks on fresh CLI
    // starts) → verification can never confirm. Say it CLEARLY + stop polling: the model
    // is persisted and the process already runs with it — it just needs a login.
    if (v?.agent_state === 'needs_login') {
      finish('info', `${sessionName}: Restart erfolgt, Modell ${requested} ist gesetzt — der Agent braucht jetzt einen LOGIN (Edit → Login), dann ist es aktiv.`);
      return;
    }
    if (v?.status === 'agent_not_ready') {
      // Agent still booting after the restart → poll the on-demand probe. Each probe
      // gets its own timeout (remote probes measured ~30s — unbounded polls kept the
      // pending toast up for minutes). Window widened to ~40s with a gentle backoff
      // (Alex 2026-07-05: heavy/remote agents boot slower than the old 4×3s=12s window,
      // so verification "lagged behind" and fell to "ausstehend" though the switch worked).
      for (let i = 0; i < 8; i++) {
        await new Promise((r) => setTimeout(r, i < 3 ? 3000 : 6000));
        try {
          const pc = new AbortController();
          const pt = setTimeout(() => pc.abort(), 12_000);
          const pr = await fetch(`${base}/api/sessions/${sessionName}/model-verify`, { signal: pc.signal });
          clearTimeout(pt);
          const pd: { model_verification?: ModelVerification } & ModelVerification = await pr.json();
          const pv = pd.model_verification ?? pd;
          if (pv?.verified) { finish('success', `✓ ${sessionName}: aktiv ${pv.active_model || requested}`); return; }
          if (pv?.agent_state === 'needs_login') {
            finish('info', `${sessionName}: Restart erfolgt, Modell ${requested} ist gesetzt — der Agent braucht jetzt einen LOGIN (Edit → Login), dann ist es aktiv.`);
            return;
          }
          if (pv?.verified === false && pv?.active_model) {
            finish('error', `${sessionName}: angefordert ${requested}, aktiv ${pv.active_model} — CLI hat das Modell nicht übernommen.`);
            return;
          }
        } catch { /* transient — keep polling */ }
      }
      finish('info', `${sessionName}: Agent noch nicht bereit — Modell-Verifikation ausstehend (später im Edit-Dialog prüfen).`);
      return;
    }
    if (v && v.verified === false) {
      finish('error', `${sessionName}: angefordert ${requested}, aktiv ${v.active_model || 'unbekannt'} — CLI hat das Modell nicht übernommen${v.detail ? ` (${v.detail})` : ''}.`);
      return;
    }
    // No verification block (unsupported agent like codex, or older node build).
    finish('info', `${sessionName}: Konfiguration gespeichert.`);
  } catch (e) {
    finish('error', `${sessionName}: Modellwechsel fehlgeschlagen — ${e instanceof Error && e.name === 'AbortError' ? 'Timeout (90 s)' : e instanceof Error ? e.message : 'Netzwerkfehler'}.`);
  }
}

/**
 * Profilwechsel — speichern, und DANN fragen, ob sofort gewechselt werden soll.
 *
 * Warum nicht wie beim Modell automatisch neu starten (Alex, 19.08.; Login und
 * ich unabhängig zum selben Schluss): Beim Modell ist der Neustart zwingend,
 * sonst wirkt die Änderung nie. Beim Profil greift sie beim nächsten regulären
 * Start ohnehin — ein Agent mitten in der Arbeit verlöre seinen Zug für nichts.
 * Also: speichern, ehrlich sagen dass es noch nicht wirkt, und den Neustart als
 * Knopf anbieten.
 *
 * Die Falle, die das schließt (Logins Befund): Zwischen Speichern und Neustart
 * zeigt die Oberfläche das NEUE Profil, während der Prozess auf dem ALTEN Konto
 * weiterläuft. Wer in der Zwischenzeit von Hand anmeldet, dessen Anmeldung wird
 * beim nächsten Start überschrieben — Alex' „am nächsten Morgen war wieder das
 * alte Konto da".
 */
async function runProfileChange(base: string, sessionName: string, profile: string) {
  const key = `profile-change:${sessionName}`;
  const label = profile || '(Standard-Zuordnung)';
  const finish = (variant: 'success' | 'error' | 'info', content: React.ReactNode) => {
    dismissToastByKey(key);
    pushToast({ dedupKey: key, variant, dismiss: 'manual', content });
  };

  // Nach dem Neustart: warten, bis der Anmelde-Zustand wieder etwas Belastbares
  // sagt. `token-status` ist die einzige Quelle, die HEUTE ohne Logins Umbau
  // etwas über das laufende Konto weiß (master_available, needs_login, verdict).
  const verifyAfterRestart = async () => {
    dismissToastByKey(key);
    pushToast({
      dedupKey: key, variant: 'pending', dismiss: 'manual',
      content: `${sessionName}: Neustart läuft, Anmeldung wird geprüft…`,
    });
    try {
      await api.sessions.restart(sessionName);
    } catch (e) {
      finish('error', `${sessionName}: Neustart fehlgeschlagen — ${e instanceof Error ? e.message : 'Netzwerkfehler'}.`);
      return;
    }
    for (let i = 0; i < 20; i++) {
      await new Promise((r) => setTimeout(r, 3000));
      try {
        const res = await fetch(`${base}/api/auth/token-status/${encodeURIComponent(sessionName)}`);
        if (!res.ok) continue;
        const t = await res.json() as {
          verdict?: string; needs_login?: boolean; master_available?: boolean; has_token?: boolean;
        };
        if (t.master_available === false) {
          finish('error', `${sessionName}: Für ${label} liegt kein Master-Token vor — die Anmeldung kann nicht gesetzt werden. (Login-Agent fragen.)`);
          return;
        }
        if (t.needs_login) {
          finish('info', `${sessionName}: Neustart erfolgt, Profil ${label} ist gesetzt — der Agent braucht jetzt einen LOGIN (Edit → Login).`);
          return;
        }
        if (t.verdict === 'healthy' && t.has_token) {
          finish('success', `✓ ${sessionName}: läuft nach dem Neustart mit ${label}.`);
          return;
        }
      } catch { /* vorübergehend — weiter prüfen */ }
    }
    finish('info', `${sessionName}: Neustart ausgelöst, der Anmelde-Zustand ist noch nicht eindeutig — später im Edit-Dialog nachsehen.`);
  };

  try {
    const res = await fetch(`${base}/api/sessions/${sessionName}/config`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ chrome_profile: profile }),
    });
    if (!res.ok) {
      finish('error', `${sessionName}: Profil konnte nicht gespeichert werden (HTTP ${res.status}).`);
      return;
    }
  } catch (e) {
    finish('error', `${sessionName}: Profil konnte nicht gespeichert werden — ${e instanceof Error ? e.message : 'Netzwerkfehler'}.`);
    return;
  }

  dismissToastByKey(key);
  pushToast({
    dedupKey: key, variant: 'info', dismiss: 'manual',
    content: (
      <span className="toast-with-action">
        {sessionName}: Profil <strong>{label}</strong> gespeichert — <strong>noch nicht aktiv</strong>.
        Der Agent läuft bis zum nächsten Start auf dem bisherigen Konto weiter.
        <button className="toast-action" onClick={() => { void verifyAfterRestart(); }}>
          Jetzt neu starten &amp; prüfen
        </button>
      </span>
    ),
  });
}

interface OwnerOption {
  email: string;
  first_name?: string;
  last_name?: string;
  is_superadmin?: boolean;
  // Tenant memberships from auth-api — list of (tenant_id, system, role)
  // tuples this user is mapped to. Empty list = no explicit membership.
  tenants?: Array<{ tenant_id: string; system: string; role: string }>;
}

interface TenantOption {
  id: string;
  display_name: string;
  email_domain: string | null;
  is_active: boolean;
}

const STORAGE_KEY = 'ark-auth';

// Hardcoded fallback for the Claude model dropdown — used only if the
// /api/models catalog fetch fails (federation hiccup at dialog-open).
// Mirrors the pre-catalog list so we never regress to an empty dropdown.
const FALLBACK_CLAUDE_MODELS = [
  'fable', 'opus', 'sonnet', 'haiku',
  'MiniMax-M3', 'deepseek-v4-pro', 'deepseek-v4-flash',
];

// Turn a raw spawn-command model string into a human label. The <option>
// VALUE stays the raw string (case-sensitive, exactly what the backend
// expects); only the visible text is prettified.
export function prettyModel(m: string): string {
  const FRIENDLY: Record<string, string> = {
    fable: 'Fable 5', opus: 'Opus', sonnet: 'Sonnet', haiku: 'Haiku',
  };
  if (FRIENDLY[m]) return FRIENDLY[m];
  if (m.startsWith('MiniMax-')) {
    // MiniMax-M2.7-highspeed → "MiniMax M2.7 Highspeed"
    return m.split('-').map((p) => (/highspeed/i.test(p) ? 'Highspeed' : p)).join(' ');
  }
  // Generic dash-split title-case with provider/version fixups:
  //   deepseek-v4-pro → "DeepSeek V4 Pro", gemini-2.5-pro → "Gemini 2.5 Pro"
  return m.split('-').map((p) => {
    if (p.toLowerCase() === 'deepseek') return 'DeepSeek';
    if (/^glm$/i.test(p)) return 'GLM';                 // glm-4.5 → GLM 4.5
    if (/^gpt$/i.test(p)) return 'GPT';
    if (/^v\d/i.test(p)) return p.toUpperCase();       // v4 → V4
    if (/^\d/.test(p)) return p;                        // 2.5 / 5.5 / 4.5 untouched
    return p.charAt(0).toUpperCase() + p.slice(1);      // pro → Pro / turbo → Turbo / air → Air
  }).join(' ');
}

interface ModelCatalog {
  claude: string[];
  codex?: string[];
  gemini?: string[];
  // Anti-Gravity CLI — a native multi-model agent (own model list: Gemini 3.5, Claude,
  // GPT, …). Replaces Gemini going forward (#157). agy = its spawn identifier.
  agy?: string[];
  providers?: {
    anthropic_native?: string[];
    minimax?: string[];
    deepseek?: string[];
    [k: string]: string[] | undefined;
  };
  // Per-provider effort catalog (Cloud 474d083 — proxied from api-ai, xhigh for
  // codex, no more hardcoded FE effort lists). Absent on older backends → the FE
  // falls back to its previous hardcoded ranges.
  efforts?: { [provider: string]: { available?: string[]; default?: string } | undefined };
  defaults?: { [provider: string]: string | undefined };
  // Capability gate (Cloud #4337): which agents actually forward the picked
  // model to their CLI as a spawn flag. cloud-api sets this ONLY once its
  // _build_command branch for that agent exists — so the codex model dropdown
  // stays dormant (hidden, honest hint) until the backend can honour it, then
  // lights up with no second FE deploy. Absent on older backends. Cloud may
  // surface it top-level or under providers_meta; tolerate both.
  supports_model_arg?: string[];
  providers_meta?: { [provider: string]: { supports_model_arg?: boolean } | undefined };
}

// Partition the Claude model list into labelled optgroups using the
// catalog's `providers` map. Models in `claude` not covered by any
// provider group fall into a trailing "Weitere" group so nothing is
// silently dropped when the backend adds a provider the FE doesn't name.
function claudeModelGroups(cat: ModelCatalog | null): Array<{ label: string; models: string[] }> {
  const claude = cat?.claude && cat.claude.length > 0 ? cat.claude : FALLBACK_CLAUDE_MODELS;
  const prov = cat?.providers;
  if (!prov) {
    return [{ label: '', models: claude }];
  }
  const groups: Array<{ label: string; models: string[] }> = [];
  const grouped = new Set<string>();
  const push = (label: string, key: string) => {
    const list = (prov[key] || []).filter((m) => claude.includes(m));
    if (list.length === 0) return;
    list.forEach((m) => grouped.add(m));
    groups.push({ label, models: list });
  };
  push('Claude', 'anthropic_native');
  push('MiniMax', 'minimax');
  push('DeepSeek', 'deepseek');
  // Any other named provider groups the backend may add later (e.g. glm,
  // landing when Cloud adds it to ANTHROPIC_COMPAT_PROVIDERS — Minimax IACP
  // c3cb679b). Prettify known keys; title-case unknown ones so the
  // optgroup never reads as a bare lowercase key.
  const PROVIDER_LABEL: Record<string, string> = { glm: 'GLM', zhipu: 'GLM' };
  for (const [k, v] of Object.entries(prov)) {
    if (['anthropic_native', 'minimax', 'deepseek'].includes(k) || !v) continue;
    const list = v.filter((m) => claude.includes(m));
    if (list.length === 0) continue;
    list.forEach((m) => grouped.add(m));
    const label = PROVIDER_LABEL[k] || (k.charAt(0).toUpperCase() + k.slice(1));
    groups.push({ label, models: list });
  }
  const leftovers = claude.filter((m) => !grouped.has(m));
  if (leftovers.length > 0) groups.push({ label: 'Weitere', models: leftovers });
  return groups;
}

function getAuthToken(): string | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw)?.access_token || null;
  } catch {
    return null;
  }
}

interface Props {
  editSession?: SessionInfo | null;
  onCreated: (name: string) => void;
  onClose: () => void;
}

export function NewSessionDialog({ editSession, onCreated, onClose }: Props) {
  const instanceMode = useInstanceMode();
  const isEdit = !!editSession;
  const [name, setName] = useState(editSession?.name || '');
  const [agent, setAgent] = useState(editSession?.agent || 'claude');
  const [model, setModel] = useState(editSession?.model || '');
  // Seed effort from the editing session when available, otherwise default
  // to 'high' for new sessions (matches the pre-existing dropdown default).
  const [effort, setEffort] = useState(editSession?.effort || 'high');
  // Per-bot Chrome-Profile override. Empty = inherit from owner-email
  // mapping (auto_login.py default). When set, this profile is used at
  // next login trigger (Cloud commits a97bd76 + 25117e2).
  const [chromeProfile, setChromeProfile] = useState(editSession?.chrome_profile || '');
  const [loginProfiles, setLoginProfiles] = useState<Array<{
    profile: string;
    provider: string;
    // null = profile slot exists but no successful login yet — guard
    // before .split('@') etc (Alex's runtime crash, commit 9883748).
    // ALSO: Cloud's backend currently redacts this field for user-JWTs
    // (returns null even when the profile IS signed in). Aliases come
    // through correctly though, so we fall back to the first alias when
    // signed_in_as is null. To be fixed properly server-side.
    signed_in_as: string | null;
    aliases?: string[];
  }>>([]);
  // Tracks whether the last catalog fetch errored — gates the retry
  // button shown next to the dropdown. Distinct from "catalog is empty"
  // since 0 profiles could also be a legitimate state.
  const [catalogError, setCatalogError] = useState(false);
  const [catalogLoading, setCatalogLoading] = useState(false);
  // Model catalog (Cloud's /api/models, registry-derived). Null until
  // the first fetch resolves — the dropdown falls back to the hardcoded
  // FALLBACK_CLAUDE_MODELS list while loading / on error so it's never
  // empty. A successful fetch surfaces the full MiniMax/DeepSeek range.
  const [modelCatalog, setModelCatalog] = useState<ModelCatalog | null>(null);

  // Fetch the login-profile catalog. Runs on mount + can be re-triggered
  // by the retry button when the cloud-api had a transient hiccup at
  // dialog-open time (federation-flake, brief 502 etc).
  const fetchProfiles = useCallback(() => {
    let cancelled = false;
    setCatalogLoading(true);
    setCatalogError(false);
    api.loginProfiles()
      .then((r) => {
        if (cancelled) return;
        setLoginProfiles(r.profiles || []);
        setCatalogLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setCatalogError(true);
        setCatalogLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const cleanup = fetchProfiles();
    return cleanup;
  }, [fetchProfiles]);

  // Fetch the model catalog on mount. Best-effort — on failure the
  // dropdown silently uses FALLBACK_CLAUDE_MODELS (still includes M3 +
  // DeepSeek), so a federation hiccup never leaves the field empty.
  useEffect(() => {
    let cancelled = false;
    api.models()
      .then((r) => { if (!cancelled) setModelCatalog(r); })
      .catch(() => { /* keep null → fallback list */ });
    return () => { cancelled = true; };
  }, []);

  const modelGroups = claudeModelGroups(modelCatalog);

  // Per-provider model lists for the MiniMax/DeepSeek UI-agent choices.
  // Sourced from the catalog's provider groups (fallback to the hardcoded
  // list partition if the catalog hasn't loaded). Alex 2026-06-18 wanted
  // MiniMax/DeepSeek as TOP-LEVEL agent choices — they spawn as agent=
  // claude + a model-swap (Cloud verified: resolve_anthropic_provider sets
  // ANTHROPIC_BASE_URL+TOKEN from the model string, no extra request
  // fields), so the "Agent" dropdown offers them and we map to claude
  // internally + scope the model picker to that provider.
  const minimaxModels = modelGroups.find((g) => g.label === 'MiniMax')?.models
    || (modelCatalog?.providers?.minimax ?? []);
  const deepseekModels = modelGroups.find((g) => g.label === 'DeepSeek')?.models
    || (modelCatalog?.providers?.deepseek ?? []);
  // GLM (Zhipu/z.ai) — landed in the catalog 2026-06-19 (Cloud commit
  // 0b23857). NO hardcoded fallback: the synthetic GLM agent option is
  // gated on the catalog actually carrying glm models, so it never shows
  // a broken empty option on a node/catalog without GLM.
  const glmModels = modelGroups.find((g) => g.label === 'GLM')?.models
    || (modelCatalog?.providers?.glm ?? []);
  const FALLBACK_MINIMAX = ['MiniMax-M3'];
  const FALLBACK_DEEPSEEK = ['deepseek-v4-pro', 'deepseek-v4-flash'];
  const minimaxList = minimaxModels.length > 0 ? minimaxModels : FALLBACK_MINIMAX;
  const deepseekList = deepseekModels.length > 0 ? deepseekModels : FALLBACK_DEEPSEEK;
  const glmList = glmModels;
  // Anti-Gravity (agy) models — top-level catalog key, its own native model list (#157).
  const agyList = modelCatalog?.agy ?? [];
  // Codex models — top-level catalog key, mirrored by cloud-api from api-ai
  // (automation-curated, ?provider=codex). Cloud passes the pick to the CLI as
  // a spawn flag: `codex --model <X> -c model_reasoning_effort=<Y> …` (post
  // #4337) — so unlike the earlier config.toml idea it re-applies on EVERY
  // (re)spawn instead of only the first one.
  const codexList = modelCatalog?.codex ?? [];
  // Only expose the codex model picker once cloud-api confirms it forwards the
  // pick to the CLI (`codex --model … -c model_reasoning_effort=…`). Until the
  // flag arrives, showing the dropdown would be a silent no-op — worse than the
  // honest "Default" hint. Tolerate both shapes Cloud floated (top-level list OR
  // providers_meta.codex.supports_model_arg).
  const codexModelArgSupported =
    (modelCatalog?.supports_model_arg?.includes('codex') ?? false) ||
    (modelCatalog?.providers_meta?.codex?.supports_model_arg ?? false);
  const codexModelPicker = agent === 'codex' && codexModelArgSupported && codexList.length > 0;

  // The UI-level "Agent" selection (`agent`) can hold the synthetic values
  // 'minimax'/'deepseek'/'glm'. The REAL agent type sent to the API is
  // always claude for those — only the model differs.
  const apiAgent = (agent === 'minimax' || agent === 'deepseek' || agent === 'glm') ? 'claude' : agent;
  const isClaudeFamily = apiAgent === 'claude';

  // A third-party compat model reached via the claude-cli base-URL swap
  // (MiniMax / DeepSeek / GLM). These authenticate via a provider API-key
  // on the node (not an OAuth Chrome-profile), so the Login-Profile field
  // is hidden. They also use `reasoning_effort` with only low/medium/high
  // — NOT claude's xhigh/max (Minimax IACP c3cb679b, z.ai verified).
  const isApiKeyModel = /^MiniMax-/i.test(model) || /^deepseek-/i.test(model) || /^glm-/i.test(model);
  const compatProviderLabel = /^MiniMax-/i.test(model) ? 'MiniMax'
    : /^glm-/i.test(model) ? 'GLM'
    : 'DeepSeek';

  // Provider for the active agent — drives the login-profile filter.
  // minimax/deepseek collapse to anthropic (claude-cli) but the profile
  // dropdown is hidden for them anyway (isApiKeyModel).
  const agentProvider = apiAgent === 'claude' ? 'anthropic'
    : apiAgent === 'codex' ? 'openai'
    : apiAgent === 'gemini' ? 'gemini'
    : apiAgent;

  // Default model for a synthetic provider agent — flagship first.
  const providerDefaultModel = useCallback((a: string): string => {
    if (a === 'minimax') return minimaxList.includes('MiniMax-M3') ? 'MiniMax-M3' : minimaxList[0];
    if (a === 'deepseek') return deepseekList.includes('deepseek-v4-pro') ? 'deepseek-v4-pro' : deepseekList[0];
    if (a === 'glm') return glmList.includes('glm-4.6') ? 'glm-4.6' : glmList[0];
    return '';
  }, [minimaxList, deepseekList, glmList]);

  // Keep `model` consistent with the chosen agent:
  //  - codex/gemini ignore model (own CLI config) → clear to "".
  //  - minimax/deepseek REQUIRE a provider model (empty would silently
  //    fall back to native Claude) → snap to the provider default unless
  //    the current model already belongs to that provider.
  //  - claude → leave model untouched (full grouped picker).
  // Runs on agent change AND when the catalog loads (lists fill in).
  //
  // EDIT-MODE GUARD (Bug, 2026-07-31): these rules exist to drop a model
  // CARRIED OVER from another agent type. In the edit dialog they were also
  // wiping a model the session legitimately already had: opening the dialog
  // seeded `model` from `editSession.model`, the codex branch cleared it
  // (the capability flag is currently off), and handleSave then PATCHed
  // `model: ''` because it differed from the stored value — the agent lost
  // its model just by opening and saving the dialog. Three live codex
  // agents (C-Weiss-Codex, Cloud-Codex, CloudV2-Codex) carry
  // `model: gpt-5.6-sol` and were affected.
  // So: in edit mode only reset once the user ACTUALLY changed the agent
  // type. An untouched dialog must never mutate a stored value.
  const agentTypeTouched = useRef(false);
  useEffect(() => {
    if (isEdit && !agentTypeTouched.current) return;
    if (agent === 'codex') {
      // Codex takes a model now (#4337) — but only when the backend honours it
      // (codexModelArgSupported). If supported: keep a valid codex pick, drop a
      // carried-over foreign model (e.g. 'opus') → "" = CLI default. If NOT yet
      // supported (flag absent): clear any model so we never submit a no-op.
      if (!codexModelArgSupported) { if (model) setModel(''); }
      else if (model && !codexList.includes(model)) setModel('');
    } else if (agent === 'gemini') {
      if (model) setModel('');
    } else if (agent === 'minimax') {
      if (!minimaxList.includes(model)) setModel(providerDefaultModel('minimax'));
    } else if (agent === 'deepseek') {
      if (!deepseekList.includes(model)) setModel(providerDefaultModel('deepseek'));
    } else if (agent === 'glm') {
      if (!glmList.includes(model)) setModel(providerDefaultModel('glm'));
    } else if (agent === 'agy') {
      // Anti-Gravity has its own models; drop a carried-over non-agy model so it falls
      // back to "" (= agy's default) — but keep a valid agy model the user picked (#157).
      if (model && !agyList.includes(model)) setModel('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agent, minimaxList.join(','), deepseekList.join(','), glmList.join(','), agyList.join(','), codexList.join(','), codexModelArgSupported]);

  // Clamp effort into the compat range: MiniMax/DeepSeek/GLM only accept
  // reasoning_effort low/medium/high, so a stale xhigh/max (e.g. carried
  // over from a native-claude selection) would be an invalid spawn arg.
  useEffect(() => {
    if (isApiKeyModel && (effort === 'xhigh' || effort === 'max')) {
      setEffort('high');
    }
  }, [isApiKeyModel, effort]);

  // Effort options are catalog-driven per provider (Cloud 474d083): codex e.g.
  // [low,medium,high,xhigh] — no 'max' (which it doesn't support). Compat models
  // stay low/medium/high. Falls back to the previous hardcoded native range when
  // the backend doesn't ship an effort catalog yet.
  const effortOptions: string[] = useMemo(() => {
    if (isApiKeyModel) return ['low', 'medium', 'high'];
    const fromCatalog = modelCatalog?.efforts?.[apiAgent]?.available;
    return (fromCatalog && fromCatalog.length > 0)
      ? fromCatalog
      : ['low', 'medium', 'high', 'xhigh', 'max'];
  }, [isApiKeyModel, modelCatalog, apiAgent]);

  // A stale effort not offered for the current provider (e.g. carried-over 'max'
  // onto codex which caps at 'xhigh') resets to the provider default → 'high'.
  useEffect(() => {
    if (!effortOptions.includes(effort)) {
      setEffort(modelCatalog?.efforts?.[apiAgent]?.default || 'high');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effortOptions]);
  const [node, setNode] = useState('');
  const [clusterNodes, setClusterNodes] = useState<Array<{ name: string; is_self: boolean }>>([]);
  const [department, setDepartment] = useState(editSession?.department || '');
  const [role, setRole] = useState(editSession?.role || '');
  const [avatarUrl, setAvatarUrl] = useState(editSession?.avatar || '');
  const [avatarPreview, setAvatarPreview] = useState(editSession?.avatar || '');
  const [displayName, setDisplayName] = useState(editSession?.display_name || '');
  const [description, setDescription] = useState(editSession?.description || '');
  const [mustBeOnline, setMustBeOnline] = useState<boolean>(!!editSession?.must_be_online);
  const [rbacConfig, setRbacConfig] = useState<RbacConfig | null>(null);
  const [mcpServers, setMcpServers] = useState<Set<string>>(new Set(['cloud', 'comm']));
  const [ownerEmail, setOwnerEmail] = useState<string>('');
  const [users, setUsers] = useState<OwnerOption[]>([]);
  const [tenants, setTenants] = useState<TenantOption[]>([]);
  // Optional explicit tenant for the new agent. Empty string ⇒ inherit
  // from owner's primary tenant (the existing default behaviour). Set
  // by the admin only when creating an agent for a tenant the OWNER
  // belongs to but the caller wants to scope manually.
  const [tenantOverride, setTenantOverride] = useState<string>('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  // Name-collision detection (#46). Populated on dialog-open with all
  // existing agent names across the federation so we can warn the
  // operator BEFORE submit instead of letting the backend silently
  // reject (or worse, accept-with-suffix and confuse the operator).
  const [existingNames, setExistingNames] = useState<Set<string>>(new Set());
  const fileRef = useRef<HTMLInputElement>(null);

  const { user } = useAuth();
  const isAdmin = !!user && (user.is_superadmin || user.agentos_role === 'admin' || ADMIN_EMAILS.includes(user.email));

  const ALL_MCP = ALL_MCP_SERVERS;

  const toggleMcp = (server: string) => {
    setMcpServers((prev) => {
      const next = new Set(prev);
      if (next.has(server)) next.delete(server);
      else next.add(server);
      return next;
    });
  };

  useEffect(() => {
    api.rbac.config().then(setRbacConfig).catch(() => {});
    api.cluster.nodes()
      // arkturian is a Hub-only node (no cloud-api) — spawning there 400s
      // with "Unknown federation node 'arkturian'" (Minimax verified
      // 2026-06-18). Filter it out of the spawn-target list regardless of
      // whether the endpoint or the fallback surfaces it.
      .then((data) => setClusterNodes((data.nodes || []).filter((n) => n.name !== 'arkturian')))
      .catch(() => {
        // Fallback: hardcoded list if endpoint missing (no arkturian).
        setClusterNodes([
          { name: 'mac', is_self: false },
          { name: 'arkserver', is_self: false },
          { name: 'oneal', is_self: false },
          { name: 'pdrei', is_self: false },
          { name: 'tommy', is_self: false },
        ]);
      });
  }, []);

  // Load MCP servers for edit mode
  useEffect(() => {
    if (isEdit && editSession) {
      api.sessions.getMcp(editSession.name)
        .then((d) => setMcpServers(new Set(Object.keys(d.servers))))
        .catch(() => {});
    }
  }, [isEdit, editSession]);

  // Name-collision pre-check (#46). Fetch all known agent names — local
  // + federation — so the operator gets a live "name already taken"
  // warning while typing. Alex's SanLumo/Sunlumo confusion was the
  // motivating incident: the backend would have silently created a
  // second "SanLumo" without surfacing that "Sunlumo" already exists.
  // We compare case-insensitive so "sanlumo"/"SanLumo" collide too.
  useEffect(() => {
    if (isEdit) return;
    Promise.all([api.sessions.all(), api.sessions.remote().catch(() => ({ sessions: [] }))])
      .then(([localResp, remoteResp]) => {
        const names = new Set<string>();
        for (const s of (localResp.sessions || [])) names.add(s.name.toLowerCase());
        for (const s of (remoteResp.sessions || [])) names.add(s.name.toLowerCase());
        setExistingNames(names);
      })
      .catch(() => setExistingNames(new Set()));
  }, [isEdit]);

  // Live collision result for the current input.
  const trimmedName = (name || '').trim();
  const nameCollision = !isEdit && trimmedName.length > 0
    && existingNames.has(trimmedName.toLowerCase());
  // Soft-warn for similar-but-not-identical names (e.g. Sunlumo vs SanLumo).
  // Triggers when an existing name differs by ≤2 character edits OR is a
  // substring relationship — cheap heuristic, no full Levenshtein needed.
  const similarTo = !isEdit && trimmedName.length >= 4 && !nameCollision
    ? Array.from(existingNames).find((n) => {
        const a = trimmedName.toLowerCase();
        if (n === a) return false;
        if (n.includes(a) || a.includes(n)) return true;
        // 1-character substitution check (cheap diff)
        if (Math.abs(n.length - a.length) > 2) return false;
        let edits = 0;
        const len = Math.min(n.length, a.length);
        for (let i = 0; i < len; i++) if (n[i] !== a[i]) edits++;
        edits += Math.abs(n.length - a.length);
        return edits <= 2;
      })
    : null;

  // Admin-only: load user list + tenant list so the Owner/Tenant pickers
  // can offer cross-tenant choices. Skipped for non-admins — they
  // always create agents under themselves.
  //
  // Auth headers are REQUIRED on both endpoints (require_superadmin /
  // require_admin_or_member). Before this fix the dialog fetched
  // without auth, got 401, and the dropdown silently fell back to
  // "— me —" only.
  useEffect(() => {
    if (!isAdmin || isEdit) return;
    const token = getAuthToken();
    const headers = token ? { Authorization: `Bearer ${token}` } : undefined;

    // Registry statt fester Betreiber-Adresse: Auf einer Kundeninstanz ist das
    // Nutzerverzeichnis IHRES Anmeldedienstes gemeint, nicht das des Betreibers.
    fetch(`${serviceOrigin('auth') || ''}/api/v1/auth/users`, { headers })
      .then((r) => (r.ok ? r.json() : { users: [] }))
      .then((d) => {
        const list: OwnerOption[] = Array.isArray(d) ? d : (d.users || []);
        setUsers(list.filter((u) => !!u.email));
      })
      .catch(() => setUsers([]));

    fetch(`${serviceOrigin('auth') || ''}/api/v1/auth/tenants`, { headers })
      .then((r) => (r.ok ? r.json() : []))
      .then((d) => {
        const list: TenantOption[] = Array.isArray(d) ? d : (d.tenants || []);
        setTenants(list.filter((t) => t.is_active !== false));
      })
      .catch(() => setTenants([]));
  }, [isAdmin, isEdit]);

  const departments = rbacConfig
    ? Object.entries(rbacConfig.departments)
    : [];

  const selectedDept = rbacConfig?.departments[department];
  const roles = selectedDept?.roles || [];

  // Avatar pipeline → log-api via the shared client. The old inline
  // fetch had the same wire-format bugs as InputBar — wrong body shape
  // and no session-create — so avatar logs have never been delivered
  // since this dialog was introduced (Cloud's IACP 0087bd88).
  const log = async (type: string, msg: string) => {
    await logToApi('CloudV2-avatar', type as LogType, msg);
  };

  const handleAvatarFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    await log('Log', `Avatar file selected: ${file.name}, size=${file.size}, type=${file.type}`);

    // Preview
    const reader = new FileReader();
    reader.onload = () => setAvatarPreview(reader.result as string);
    reader.readAsDataURL(file);

    // Upload via local API to dedicated avatar endpoint (no TTL, persistent
    // collection). Falls back to generic /api/upload if avatar endpoint isn't
    // deployed yet on this node — same response shape, just with TTL.
    try {
      let res = await api.uploadRaw('/api/upload/avatar', file);
      // Older nodes return 404 (no route), some return 405 (method-not-allowed
      // because the path is reserved by some middleware). Fall back to the
      // generic /api/upload endpoint in either case.
      if (!res.ok && (res.status === 404 || res.status === 405)) {
        res = await api.uploadRaw('/api/upload', file);
      }
      const data = await res.json();
      await log('Log', `Upload response: ${JSON.stringify(data).slice(0, 200)}`);
      if (data.path) {
        setAvatarUrl(data.path);
      }
    } catch (err) {
      await log('Error', `Upload failed: ${err}`);
    }
    e.target.value = '';
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      let sessionName = name;

      if (isEdit) {
        sessionName = editSession!.name;
        const base = editSession?.node_url || '';
        await log('Log', `Saving edit: session=${sessionName}, base=${base}, avatarUrl=${avatarUrl}, oldAvatar=${editSession?.avatar || ''}`);
        // Update avatar
        if (avatarUrl !== (editSession?.avatar || '')) {
          const avatarEndpoint = `${base}/api/sessions/${sessionName}/avatar`;
          await log('Log', `PUT avatar to: ${avatarEndpoint}`);
          const avatarRes = await fetch(avatarEndpoint, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ avatar: avatarUrl }),
          });
          await log('Log', `Avatar PUT response: ${avatarRes.status} ${await avatarRes.text()}`);
        }
        // Update display_name + must_be_online + model + effort via config
        // (single PATCH). Backend allow-set was extended by Cloud's commit
        // 742cb2b — model + effort persist on the registry row and pick up
        // at the next bot restart. We send them whenever the user changed
        // them in the dialog (incl. clearing model back to default "").
        const cfgPatch: Record<string, unknown> = {};
        if (displayName !== (editSession?.display_name || '')) {
          cfgPatch.display_name = displayName;
        }
        if (description !== (editSession?.description || '')) {
          cfgPatch.description = description;
        }
        if (mustBeOnline !== !!editSession?.must_be_online) {
          cfgPatch.must_be_online = mustBeOnline;
        }
        if (model !== (editSession?.model || '')) {
          cfgPatch.model = model;
        }
        if (effort !== (editSession?.effort || 'high')) {
          cfgPatch.effort = effort;
        }
        if (chromeProfile !== (editSession?.chrome_profile || '')) {
          cfgPatch.chrome_profile = chromeProfile;
        }
        // Profil getrennt behandeln: eigener Ablauf mit Rückfrage statt stillem
        // Speichern (siehe runProfileChange). Nur wenn NICHT ohnehin ein
        // Modellwechsel läuft — der startet bereits neu und übernimmt das Profil
        // dabei mit.
        const profileOnly = cfgPatch.chrome_profile !== undefined && cfgPatch.model === undefined;
        if (profileOnly) {
          const prof = String(cfgPatch.chrome_profile ?? '');
          delete cfgPatch.chrome_profile;
          void runProfileChange(base, sessionName, prof);
        }
        if (Object.keys(cfgPatch).length > 0) {
          if (cfgPatch.model !== undefined) {
            // Model change → backend auto-restarts + verifies (Cloud 5d976b7, 8-45s).
            // Run async: the dialog closes now, progress + verdict show as global
            // toasts (pending → ✓ aktiv / warn / applies-on-next-start).
            void runModelChange(base, sessionName, cfgPatch);
          } else {
            await fetch(`${base}/api/sessions/${sessionName}/config`, {
              method: 'PATCH',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(cfgPatch),
            });
          }
        }
      } else {
        const res = await api.sessions.create({
          // Kein Rückfall auf einen Server-Vorgabenamen mehr: Der Knopf ist
          // ohne Namen gesperrt, und falls doch etwas durchkäme, soll der
          // Aufruf scheitern statt still einen zweiten „claude" anzulegen.
          name: trimmedName,
          // `apiAgent`, NOT the raw UI selection: the synthetic
          // 'minimax'/'deepseek' choices spawn as agent=claude + the
          // model-swap (Cloud + Minimax both verified — registry resolves
          // ANTHROPIC_BASE_URL+TOKEN from the model string, no extra body).
          agent: apiAgent,
          // Model + effort were silently dropped before: the dialog
          // showed them, but the API body type didn't include them so
          // the agent always landed on the CLI's default Opus + high.
          // The form values land in the body now and the backend
          // appends `--model <X>` and `--effort <Y>` to the spawn
          // command + persists both onto the registry row so a later
          // restart respects them too.
          model: model || undefined,
          effort: effort || undefined,
          chrome_profile: chromeProfile || undefined,
          description: description || undefined,
          node: node || undefined,
          department: department || undefined,
          role: role || undefined,
          // The "— me ({email}) —" picker option has value="" which would
          // submit an empty owner_email. Fall back to the caller's own
          // email so the agent always lands with an owner — required for
          // the next /login to pick the right Chrome profile.
          owner_email: ownerEmail || user?.email || undefined,
          // Optional cross-tenant assignment (Task #45). Admins use this
          // when spawning an agent for a tenant other than the owner's
          // primary tenant. Empty string ⇒ backend derives tenant from
          // owner's user_system_roles (the historical behaviour).
          tenant_id: tenantOverride || undefined,
        });
        sessionName = res.name;

        // Set avatar after creation
        if (avatarUrl) {
          await fetch(`/api/sessions/${sessionName}/avatar`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ avatar: avatarUrl }),
          }).catch(() => {});
        }
      }

      // Set MCP servers
      if (mcpServers.size > 0) {
        await api.sessions.setMcp(sessionName, Array.from(mcpServers)).catch(() => {});
      }

      onCreated(sessionName);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed');
      setSaving(false);
    }
  };

  return (
    <>
      <div className="dialog-backdrop" onClick={onClose} />
      <div className="new-session-dialog">
        <div className="dialog-header">
          <div className="dialog-title">{isEdit ? 'Edit Agent' : 'New Agent'}</div>
          <button className="dialog-close" onClick={onClose}><X size={16} /></button>
        </div>

        <div className="dialog-body">
          {/* Avatar */}
          <div className="dialog-avatar-row">
            <div className="dialog-avatar">
              {avatarPreview ? (
                <img src={avatarPreview} alt="" />
              ) : (
                <span className="dialog-avatar-placeholder">+</span>
              )}
              <input
                type="file"
                accept="image/*"
                className="dialog-avatar-overlay-input"
                onChange={handleAvatarFile}
              />
            </div>
            <div className="dialog-avatar-info">
              <div className="dialog-label">Avatar</div>
              {avatarPreview && (
                <button
                  className="dialog-avatar-btn dialog-avatar-remove"
                  onClick={() => { setAvatarUrl(''); setAvatarPreview(''); }}
                >
                  Remove
                </button>
              )}
            </div>
          </div>

          <div className="dialog-form-grid">
            <div className="dialog-field">
              <label className="dialog-label">{isEdit ? 'Session' : 'Name'}</label>
              <input
                type="text"
                className={`dialog-input ${nameCollision ? 'dialog-input-error' : similarTo ? 'dialog-input-warn' : ''}`}
                placeholder="Name des Agenten (Pflicht)"
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={isEdit}
              />
              {!isEdit && !trimmedName && (
                <div className="dialog-field-hint">
                  Der Name ist die ADRESSE des Agenten — das Portal spricht ihn
                  überall darüber an. Ohne Eingabe vergab bisher der Server einen
                  Vorgabenamen (z. B. „claude"), und der kollidierte still mit
                  einem bestehenden Agenten auf einem anderen Knoten: Danach gab
                  es zwei gleichnamige, und einer war nicht mehr erreichbar
                  (Alex, 19.08.). Deshalb Pflichtfeld.
                </div>
              )}
              {nameCollision && (
                <div className="dialog-field-error">
                  ⛔ Name „<strong>{trimmedName}</strong>" ist bereits in der Federation vergeben. Wähle einen anderen.
                </div>
              )}
              {!nameCollision && similarTo && (
                <div className="dialog-field-warn">
                  ⚠️ Sehr ähnlich zu existierendem Agent „<strong>{similarTo}</strong>". Falls nicht beabsichtigt — Tippfehler?
                </div>
              )}
            </div>

            <div className="dialog-field">
              <label className="dialog-label">Display Name</label>
              <input
                type="text"
                className="dialog-input"
                placeholder="Optional friendly name"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
              />
            </div>

            <div className="dialog-field" style={{ gridColumn: '1 / -1' }}>
              <label className="dialog-label">Description</label>
              <textarea
                className="dialog-input"
                placeholder="Was macht dieser Agent? 1-2 Sätze — wird in tm list und Agent-Suchen angezeigt."
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={2}
                style={{ resize: 'vertical', fontFamily: 'inherit' }}
              />
            </div>

            {!isEdit && (
              <div className="dialog-field">
                <label className="dialog-label">Agent</label>
                <select
                  className="dialog-select"
                  value={agent}
                  onChange={(e) => {
                    // Marks a REAL user-driven type change, which is what
                    // licenses the model-reset rules to run in edit mode.
                    agentTypeTouched.current = true;
                    setAgent(e.target.value);
                  }}
                >
                  {/* Native CLIs (own OAuth login) vs. provider-swap agents.
                    * MiniMax/DeepSeek spawn as agent=claude with an
                    * ANTHROPIC_BASE_URL+TOKEN swap (Cloud verified) — exposed
                    * here as first-class choices so you pick "a DeepSeek
                    * agent" directly instead of hunting for the model under
                    * Claude (Alex 2026-06-18). */}
                  <optgroup label="Nativ (eigenes Login)">
                    <option value="claude">Claude</option>
                    <option value="codex">Codex</option>
                    <option value="gemini">Gemini</option>
                    {/* Anti-Gravity (agy) — gated on the catalog carrying agy models, so
                        no broken option on a node without it (#157). Replaces Gemini. */}
                    {agyList.length > 0 && <option value="agy">Anti-Gravity</option>}
                  </optgroup>
                  <optgroup label="Weitere Provider (via Claude-CLI, API-Key)">
                    <option value="minimax">MiniMax</option>
                    <option value="deepseek">DeepSeek</option>
                    {/* GLM gated on catalog presence — only offered when
                      * cloud-api's registry actually resolves glm models
                      * (Cloud 0b23857), so no broken empty option appears
                      * on a node/catalog without it. */}
                    {glmList.length > 0 && <option value="glm">GLM</option>}
                  </optgroup>
                </select>
                {(agent === 'minimax' || agent === 'deepseek' || agent === 'glm') && (
                  <div className="dialog-field-hint">
                    Läuft als Claude-CLI mit{' '}
                    {agent === 'minimax' ? 'MiniMax' : agent === 'glm' ? 'GLM' : 'DeepSeek'}-Model-Swap.
                    Authentifiziert über einen Provider-API-Key am Ziel-Node — fehlt der Key dort,
                    meldet der Spawn das mit einer klaren Fehlermeldung (wähl dann einen Node mit Key).
                  </div>
                )}
              </div>
            )}

            {/* Model + Effort — shown in BOTH create and edit modes.
              * Cloud-API commit 742cb2b put both in the allowed-patch
              * field-set, so we can persist edits on existing sessions
              * via `PATCH /api/sessions/{name}/config`. Changes take
              * effect at the next bot restart (CLI args are baked at
              * spawn time, not hot-reloaded). */}
            <div className="dialog-field">
              <label className="dialog-label">Model</label>
              <select className="dialog-select" value={model} onChange={(e) => setModel(e.target.value)}>
                {/* Catalog-driven (Cloud's /api/models, registry-derived):
                  * adding a model/provider backend-side surfaces it here
                  * with no FE deploy. The option set is scoped to the chosen
                  * agent:
                  *  - claude → "Default" + all groups (native + MiniMax +
                  *    DeepSeek, since those route via the claude-cli swap)
                  *  - minimax/deepseek → ONLY that provider's models, no
                  *    empty "Default" (an empty model would silently fall
                  *    back to native Claude — Cloud's caveat)
                  *  - codex/gemini → just "Default" (own CLI config)
                  * Labels prettified; <option> values stay raw spawn strings. */}
                {isClaudeFamily && agent === 'claude' && (
                  <>
                    <option value="">Default</option>
                    {modelGroups.map((g) => (
                      g.label
                        ? (
                          <optgroup key={g.label} label={g.label}>
                            {g.models.map((m) => (
                              <option key={m} value={m}>{prettyModel(m)}</option>
                            ))}
                          </optgroup>
                        )
                        : g.models.map((m) => (
                          <option key={m} value={m}>{prettyModel(m)}</option>
                        ))
                    ))}
                  </>
                )}
                {agent === 'minimax' && minimaxList.map((m) => (
                  <option key={m} value={m}>{prettyModel(m)}</option>
                ))}
                {agent === 'deepseek' && deepseekList.map((m) => (
                  <option key={m} value={m}>{prettyModel(m)}</option>
                ))}
                {agent === 'glm' && glmList.map((m) => (
                  <option key={m} value={m}>{prettyModel(m)}</option>
                ))}
                {/* Anti-Gravity models — already human-readable labels (#157), shown raw. */}
                {agent === 'agy' && agyList.map((m) => (
                  <option key={m} value={m}>{m}</option>
                ))}
                {!isClaudeFamily && <option value="">Default</option>}
                {/* Codex models AFTER the empty "Default" so the CLI-default stays
                  * the first/neutral entry, same order as the claude picker.
                  * Gated on codexModelArgSupported — dormant until cloud-api can
                  * forward the pick (#4337). */}
                {codexModelPicker && codexList.map((m) => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </select>
              {agent === 'agy' ? (
                <div className="dialog-field-hint">
                  Anti-Gravity — eigene Modell-Liste (Gemini/Claude/GPT via Anti-Gravity-CLI). „Default" nutzt das CLI-Standardmodell.
                </div>
              ) : agent === 'codex' ? (
                <div className="dialog-field-hint">
                  {codexModelPicker
                    ? 'Modell wird beim Start als CLI-Flag gesetzt (Wechsel = Restart). „Default“ überlässt die Wahl der Codex-CLI. Effort ist unabhängig davon wählbar.'
                    : 'Modellwahl für Codex folgt in Kürze — aktuell nutzt Codex sein CLI-Standardmodell. Effort ist bereits wählbar.'}
                </div>
              ) : !isClaudeFamily && (
                <div className="dialog-field-hint">
                  Model-Auswahl gilt nur für Claude — Gemini nutzt seine eigene CLI-Konfig.
                </div>
              )}
              {isApiKeyModel && (
                <div className="dialog-field-hint">
                  Drittanbieter via Claude-CLI — Context-Cap 200k Token (Claude-Code-Limit,
                  unabhängig vom nativen Model-Maximum).
                </div>
              )}
            </div>

            <div className="dialog-field">
              <label className="dialog-label">Effort</label>
              <div className="dialog-effort-row">
                {/* Native claude: low/medium/high/xhigh/max (2.1.173, IACP
                  * 0f597682). Compat models (MiniMax/DeepSeek/GLM) use
                  * `reasoning_effort` which only accepts low/medium/high —
                  * xhigh/max are undocumented at the provider (Minimax IACP
                  * c3cb679b), so we don't offer them there. */}
                {effortOptions.map((e) => (
                  <button
                    key={e}
                    className={`dialog-effort-btn${effort === e ? ' active' : ''}`}
                    onClick={() => setEffort(e)}
                  >
                    {e}
                  </button>
                ))}
              </div>
              {isApiKeyModel && (
                <div className="dialog-field-hint">
                  {compatProviderLabel} nutzt reasoning_effort (low/medium/high).
                </div>
              )}
            </div>

            {isEdit && (
              (model !== (editSession?.model || '') || effort !== (editSession?.effort || 'high'))
            ) && (
              <div className="dialog-field-hint" style={{ marginTop: -8 }}>
                Model/Effort wird in der Registry gespeichert — wirkt erst nach dem nächsten Bot-Restart.
              </div>
            )}

            {/* Login-Profile dropdown — per-bot Chrome-Profile override.
              * Catalog filtered by provider matching the agent type. Empty
              * = inherit from owner-email mapping (= today's behaviour).
              * Runtime-read at next auto-login trigger, no restart needed.
              * Hidden for API-key models (MiniMax/DeepSeek): they auth via
              * the node-env provider key, not an OAuth Chrome profile. */}
            {isApiKeyModel ? (
              <div className="dialog-field">
                <label className="dialog-label">Login-Profile</label>
                <div className="dialog-field-hint">
                  {compatProviderLabel} authentifiziert über den
                  Provider-API-Key am Node — kein OAuth-Login-Profile nötig.
                </div>
              </div>
            ) : (
              <div className="dialog-field">
                <label className="dialog-label">Login-Profile</label>
              <div style={{ display: 'flex', gap: 6 }}>
                <select
                  className="dialog-select"
                  style={{ flex: 1 }}
                  value={chromeProfile}
                  onChange={(e) => setChromeProfile(e.target.value)}
                  disabled={catalogLoading}
                >
                  <option value="">
                    {catalogLoading
                      ? '(lade…)'
                      : catalogError
                        ? '(Katalog nicht erreichbar — nutzt Owner-Default)'
                        : loginProfiles.length === 0
                          ? '(keine Profile im Katalog)'
                          : '— Owner-Default —'}
                  </option>
                  {loginProfiles
                    .filter((p) => p.provider === agentProvider)
                    .map((p) => {
                      // Local-part only — full email gets truncated by
                      // native <select> rendering on macOS/Safari (the
                      // visible width is fixed). Full address shown as
                      // tooltip + repeated in the field-hint below the
                      // dropdown when selected.
                      //
                      // Fallback chain when signed_in_as is null:
                      //   1. First alias (Cloud's backend currently
                      //      redacts signed_in_as for user-JWTs but
                      //      aliases comes through — see IACP TODO).
                      //   2. (unsigned) if no aliases either.
                      const displayEmail = p.signed_in_as
                        || (p.aliases && p.aliases.length > 0 ? p.aliases[0] : '');
                      const localPart = displayEmail
                        ? displayEmail.split('@')[0]
                        : '(unsigned)';
                      const tooltipParts: string[] = [];
                      if (p.signed_in_as) tooltipParts.push(`signed in as: ${p.signed_in_as}`);
                      if (p.aliases && p.aliases.length > 0) {
                        tooltipParts.push(`aliases: ${p.aliases.join(', ')}`);
                      }
                      const title = tooltipParts.join('\n') || p.profile;
                      return (
                        <option key={p.profile} value={p.profile} title={title}>
                          {p.profile} · {localPart}
                        </option>
                      );
                    })}
                </select>
                {catalogError && (
                  <button
                    type="button"
                    className="dialog-effort-btn"
                    onClick={() => fetchProfiles()}
                    disabled={catalogLoading}
                    title="Profile-Katalog neu laden"
                  >
                    ↻
                  </button>
                )}
              </div>
              {catalogError && (
                <div className="dialog-field-hint">
                  Federation-Hiccup beim Laden. Owner-Default-Login funktioniert trotzdem — Retry für Multi-Account-Auswahl.
                </div>
              )}
              {!catalogError && chromeProfile && (() => {
                // Echo the full signed-in-as email beneath the dropdown
                // so the user can verify which account they picked
                // (native <select> truncates the option text on macOS).
                // Falls back to listing the aliases when signed_in_as is
                // redacted (= current Cloud backend bug for user-JWTs).
                const picked = loginProfiles.find((p) => p.profile === chromeProfile);
                if (!picked) return null;
                if (picked.signed_in_as) {
                  return <div className="dialog-field-hint">→ {picked.signed_in_as}</div>;
                }
                if (picked.aliases && picked.aliases.length > 0) {
                  return (
                    <div className="dialog-field-hint">
                      → aliases: {picked.aliases.join(', ')}
                    </div>
                  );
                }
                return <div className="dialog-field-hint">→ (noch nicht eingeloggt)</div>;
              })()}
              {!catalogError && agent === 'claude' && loginProfiles.filter((p) => p.provider === 'anthropic').length === 0 && loginProfiles.length > 0 && (
                <div className="dialog-field-hint">Keine Anthropic-Profile im Katalog.</div>
              )}
              {/* Diagnostic banner suppressed — Cloud's backend currently
                  redacts signed_in_as for user-JWTs (reported upstream).
                  Aliases still come through correctly and the dropdown
                  shows the first alias as a usable identifier. */}
              {!catalogError && agent !== 'claude' && (
                <div className="dialog-field-hint">
                  {agent === 'codex' ? 'OpenAI-Profile' : 'Gemini-Profile'} — Multi-Account-Support
                  je nach Provider-Setup.
                </div>
              )}
              </div>
            )}

            {isEdit && chromeProfile !== (editSession?.chrome_profile || '') && (
              <div className="dialog-field-hint" style={{ marginTop: -8 }}>
                Login-Profile-Wechsel wirkt beim nächsten Login-Versuch (kein Bot-Restart nötig).
              </div>
            )}

            {!isEdit && (
              <>
                {/* „Run on" ist eine Föderations-Frage. Eine Standalone-Instanz
                    hat genau eine Antwort — die Frage zu stellen hieße, dem
                    Kunden beim ersten Agenten ein Konzept zu erklären, das es
                    bei ihm nicht gibt. Leerer Wert = local = diese Instanz.
                    NUR dieses Feld ist gegated, nicht das ganze Fragment —
                    die Admin-Felder dahinter gelten auch standalone. */}
                {instanceMode === 'federation' && (
                <div className="dialog-field">
                  <label className="dialog-label">Run on</label>
                  <select className="dialog-select" value={node} onChange={(e) => setNode(e.target.value)}>
                    <option value="">local</option>
                    {clusterNodes.filter((n) => !n.is_self).map((n) => (
                      <option key={n.name} value={n.name}>{n.name}</option>
                    ))}
                  </select>
                </div>
                )}

                {isAdmin && (
                  <>
                    <div className="dialog-field">
                      <label className="dialog-label">Owner</label>
                      <select
                        className="dialog-select"
                        value={ownerEmail}
                        onChange={(e) => {
                          setOwnerEmail(e.target.value);
                          // Reset tenant-override when owner changes so the
                          // dropdown re-derives from the new owner's tenants.
                          setTenantOverride('');
                        }}
                      >
                        <option value="">— me ({user?.email}) —</option>
                        {users
                          .filter((u) => u.email !== user?.email)
                          .map((u) => {
                            const display = [u.first_name, u.last_name].filter(Boolean).join(' ').trim();
                            const tenants = (u.tenants || []).map((t) => t.tenant_id);
                            const tenantBadge = tenants.length > 0
                              ? ` · [${Array.from(new Set(tenants)).join(', ')}]`
                              : ' · [no tenant]';
                            const sa = u.is_superadmin ? ' ★' : '';
                            return (
                              <option key={u.email} value={u.email}>
                                {display ? `${display} (${u.email})${sa}${tenantBadge}` : `${u.email}${sa}${tenantBadge}`}
                              </option>
                            );
                          })}
                      </select>
                    </div>

                    <div className="dialog-field">
                      <label className="dialog-label">
                        Tenant <span style={{ opacity: 0.6, fontWeight: 'normal' }}>(optional override)</span>
                      </label>
                      <select
                        className="dialog-select"
                        value={tenantOverride}
                        onChange={(e) => setTenantOverride(e.target.value)}
                      >
                        <option value="">— derive from owner —</option>
                        {tenants.map((t) => (
                          <option key={t.id} value={t.id}>
                            {t.display_name} ({t.id})
                          </option>
                        ))}
                      </select>
                    </div>
                  </>
                )}

                <div className="dialog-field">
                  <label className="dialog-label">Department</label>
                  <select
                    className="dialog-select"
                    value={department}
                    onChange={(e) => { setDepartment(e.target.value); setRole(''); }}
                  >
                    <option value="">None</option>
                    {departments.map(([key, dept]) => (
                      <option key={key} value={key}>{dept.display_name}</option>
                    ))}
                  </select>
                </div>

                <div className="dialog-field">
                  <label className="dialog-label">Role</label>
                  <select
                    className="dialog-select"
                    value={role}
                    onChange={(e) => setRole(e.target.value)}
                    disabled={!department}
                  >
                    <option value="">Default</option>
                    {roles.map((r) => (
                      <option key={r.slug} value={r.slug}>{r.name}</option>
                    ))}
                  </select>
                </div>
              </>
            )}
          </div>

          {/* Lifecycle (edit-mode only) */}
          {isEdit && (
            <div className="dialog-lifecycle-section">
              <div className="dialog-label">Lifecycle</div>
              <label className="dialog-toggle-row">
                <div className="dialog-toggle-info">
                  <div className="dialog-toggle-label">Must Be Online</div>
                  <div className="dialog-toggle-desc">
                    Auto-Login bei OAuth-Expiry + nightly health-ping. Für Bots die immer erreichbar sein müssen.
                  </div>
                </div>
                <button
                  type="button"
                  className={`dialog-toggle-switch${mustBeOnline ? ' active' : ''}`}
                  onClick={() => setMustBeOnline(!mustBeOnline)}
                  aria-pressed={mustBeOnline}
                />
              </label>
            </div>
          )}

          {/* MCP Servers */}
          <div className="dialog-mcp-section">
            <div className="dialog-label">MCP Servers</div>
            <div className="dialog-mcp-grid">
              {ALL_MCP.map((server) => (
                <label key={server} className="dialog-mcp-item">
                  <input
                    type="checkbox"
                    checked={mcpServers.has(server)}
                    onChange={() => toggleMcp(server)}
                  />
                  <span className="dialog-mcp-name">{server}</span>
                </label>
              ))}
            </div>
          </div>

          {!isEdit && selectedDept && role && (
            <div className="dialog-permissions">
              <div className="dialog-label">Permissions</div>
              <div className="dialog-perm-tags">
                {roles.find((r) => r.slug === role)?.permissions.map((p) => (
                  <span key={p} className="dialog-perm-tag">{p}</span>
                )) || <span className="dialog-perm-tag">All (admin)</span>}
              </div>
            </div>
          )}

          {error && <div className="dialog-error">{error}</div>}
        </div>

        <div className="dialog-footer">
          <button className="dialog-btn-secondary" onClick={onClose}>Cancel</button>
          <button
            className="dialog-btn-primary"
            onClick={handleSave}
            disabled={saving || nameCollision || (!isEdit && !trimmedName)}
            title={nameCollision ? `Name "${trimmedName}" already in use` : undefined}
          >
            {saving ? 'Saving...' : isEdit ? 'Save' : 'Create'}
          </button>
        </div>
      </div>
    </>
  );
}
